<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import VuePdfEmbed from "vue-pdf-embed";
import { useRouter } from "vue-router";

import moment from "moment-timezone";

import { useSignedUrl } from "@/composables/useSignedUrl";
import { ROUTES } from '@/constants/routes';
// Helpers and types
import { formattedAddress } from "@/helpers/address";
import { downloadLink } from "@/helpers/file";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";

import type { WorkerDocument } from "@/types/documents";
import type { IMissionDB } from '@/types/mission';
import type { IInterimDB } from '@/types/worker';

import { AvatarSize } from "@/dsComponents/Avatar";
import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Card from "@/dsComponents/cards/Card.vue";

import InterimAvailabilities from "@/components/interim/InterimAvailabilities.vue";
// Components
import InterimMissions from "@/components/interim/InterimMissions.vue";

import Pulse from '../loader/Pulse.vue';

interface Props {
    interimId: string;
    mission?: IMissionDB;
    map?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    mission: undefined,
    map: false,
});

const emit = defineEmits<(e: "close") => void>();

const configStore = useConfigurationStore();
const router = useRouter();
const { t } = useI18n();

const loading = ref(false);
const interim = ref<IInterimDB | null>(null);
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const interimMissions = ref<any>({});
const showCV = ref(false);

const pdfConfig = {
    height: "100%",
    width: "100%",
};

// Store getters and state
const getJobOption = (jobId: string) => configStore.getJobOption(jobId) || { label: "", value: "" };

// Computed properties
const interimAddress = computed(() => {
    return formattedAddress(interim.value?.address);
});

const cvUrl = useSignedUrl(() => interim.value?.cv?.path);

const workerDocuments = computed(() => {
    if (!interim.value?.workerDocuments) return [];
    return Object.entries(interim.value.workerDocuments);
});

// Methods
const openCv = () => {
    showCV.value = true;
};

const downloadCV = async () => {
    if (cvUrl.value) downloadLink(cvUrl.value);
};

const close = () => {
    emit("close");
};

const goToInterim = () => {
    close();
    setTimeout(() => {
        router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: interim.value?.id || "" } });
    }, 150);
};

const formatDocumentDate = (habilitation: WorkerDocument) => {
    return moment(habilitation.expirationDate).format(t("date.format"));
};

const isExpiredDocument = (expirationDate: number) => {
    return moment(moment().valueOf()).isAfter(moment(expirationDate));
};

const init = async () => {
    loading.value = true;
    try {
        interim.value = await workerService.getWorker(props.interimId);

        if (props.mission && interim.value) {
            interimMissions.value = await workerService.loadInterimMissions({
                interimId: interim.value.id,
                missionId: props.mission.id,
            });
        }
    } catch (e) {
        console.log(e);
    }
    loading.value = false;
};

onBeforeMount(() => {
    init();
});
</script>
<template>
    <div :class="['InterimMiniRead', { inMap: map }]">
        <div class="icon" v-if="!map">
            <icon icon="cross" @click.stop="close" />
        </div>
        <Pulse v-if="loading" />
        <div v-else-if="interim">
            <div class="cv" v-if="showCV">
                <div class="close">
                    <icon icon="cross" @click="showCV = false" />
                </div>
                <VuePdfEmbed v-if="cvUrl" :source="cvUrl" :style="pdfConfig" />
            </div>

            <div class="top">
                <div class="logo">
                    <Avatar :imageUrl="interim.picture" :size="AvatarSize.size80" :firstname="interim.firstname" :lastname="interim.lastname" />
                </div>
                <div class="infos">
                    <div class="name">{{ interim.firstname }} {{ interim.lastname }}</div>
                    <div class="address">
                        {{ interimAddress }}
                    </div>
                    <div class="email" v-if="interim.email">
                        <a target="_blank" :href="`mailto:${interim.email}`">{{ interim.email }}</a>
                    </div>
                    <div class="phone" v-if="interim.phone">
                        <a target="_blank" :href="`tel:${interim.phone}`">{{ interim.phone }}</a>
                    </div>
                    <div class="cv-links" v-if="interim.cv.path">
                        <action class="grey" icon="agencyProfile" @click="openCv" />
                        <action class="grey" icon="download" @click="downloadCV" />
                    </div>
                </div>
            </div>
            <div class="disponibilities" v-if="interim.vehicle || interim.nightShift || interim.weekendShift">
                <div :class="['disponibility', { active: interim.vehicle }]" v-if="interim.vehicle">
                    <div class="name">{{ t("page.interim.hasVehicle") }}</div>
                    <div class="value"><icon :class="interim.vehicle ? 'purple' : 'grey'" :icon="interim.vehicle ? 'valid' : 'cross'" /></div>
                </div>
                <div :class="['disponibility', { active: interim.nightShift }]" v-if="interim.nightShift">
                    <div class="name">{{ t("page.interim.hasNightAvailable") }}</div>
                    <div class="value">
                        <icon :class="interim.nightShift ? 'purple' : 'grey'" :icon="interim.nightShift ? 'valid' : 'cross'" />
                    </div>
                </div>
                <div :class="['disponibility', { active: interim.weekendShift }]" v-if="interim.weekendShift">
                    <div class="name">{{ t("page.interim.hasWeekendAvailable") }}</div>
                    <div class="value">
                        <icon :class="interim.weekendShift ? 'purple' : 'grey'" :icon="interim.weekendShift ? 'valid' : 'cross'" />
                    </div>
                </div>
            </div>
            <div class="jobs" v-if="interim.jobs?.length">
                <h3 class="subtitle">{{ t("form.jobs") }}</h3>
                <div class="job" v-for="(job, index) in interim.jobs" :key="index">
                    <div class="top-job">
                        <div class="jobname">{{ getJobOption(job.jobId).label }}</div>
                        <div class="jobxp" v-if="job.experience">{{
                        // biome-ignore lint/style/useTemplate: Init Biome
                        t("jobExperience." + job.experience)
                        }}</div>
                    </div>
                </div>
            </div>
            <div class="workerDocumentsContainer">
                <h3 class="subtitle">{{ t("form.habilitations") }}</h3>
                <div v-for="(habilitationGroup, indexGroup) in workerDocuments" :key="indexGroup" class="workerDocumentsGroup">
                    <div class="group-title">{{
                        // biome-ignore lint/style/useTemplate: Init Biome
                        t("page.interim.administrativeDocuments." + habilitationGroup[0])
                    }}</div>
                    <p v-if="!habilitationGroup[1]?.length">
                        {{ t("errors.Documents.NotExist._default") }}
                    </p>
                    <Card
                        v-else
                        v-for="(doc, indexDocument) in habilitationGroup[1]"
                        :key="indexDocument"
                        class="workerDocuments"
                        :title="doc.documentName"
                    >
                        <div v-if="doc.expirationDate" :class="{ expired: isExpiredDocument(doc.expirationDate) }">
                            {{
                                // biome-ignore lint/style/useTemplate: Init Biome
                                "Expire le " + formatDocumentDate(doc)
                            }}
                        </div>
                    </Card>
                </div>
            </div>
            <InterimMissions :interimMissions="interimMissions" />
            <div class="availabilities">
                <h3 class="subtitle">{{ t("page.interim.nav.availabilities") }}</h3>
                <InterimAvailabilities :interim="interim" @close="close" />
            </div>
            <div class="actions">
                <ButtonAlt variant="filled" size="sm" type="primary" @click="goToInterim" left-icon="search">
                    {{ t("page.interims.viewDetail") }}
                </ButtonAlt>
            </div>
        </div>
        <div v-else>
            <error :error="t('global.serverError')" />
        </div>
    </div>
</template>
<style lang="scss" scoped>
.InterimMiniRead {
    position: relative;
    .cv {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: $white;
        z-index: 999;

        :deep(.Icon) {
            position: absolute;
            right: $padding;
            top: $padding;
            z-index: 99999999;
            background: $white;
            border-radius: 40px;
            height: 40px;
            width: 40px;
            display: flex;
            cursor: pointer;
            align-items: center;
            justify-content: center;
            box-shadow: 0 7px 10px 0 $darkgrey2;
            transition: 0.5s ease;

            &:hover {
                top: calc(#{$padding} - 2px);
                box-shadow: 0 9px 12px 0 $darkgrey2;
            }
        }
    }
    .actions {
        display: flex;
        justify-content: center;
    }
    .icon {
        width: 100%;
        text-align: right;
        :deep(.Icon) {
            display: inline-block;
            cursor: pointer;
            svg {
                fill: $darkgrey2;
            }

            &,
            svg {
                width: 24px;
                height: 24px;
            }
        }
    }
    .head-inner {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .inner-content {
        width: calc(100% - 90px);
    }

    &.inMap {
        .informations {
            margin-bottom: 16px;
            padding-bottom: 16px;
        }
        .interim-infos {
            & > div {
                margin-bottom: 16px;
            }
        }
    }

    .top {
        display: flex;
        align-items: start;
        color: $darkgrey;
        margin-bottom: $padding;
        width: 100%;

        .infos {
            margin-left: $padding;
            display: flex;
            flex-direction: column;
            justify-content: center;
            & > div {
                margin-bottom: 16px;

                &:last-child {
                    margin-bottom: 0;
                }

                &.phone {
                    margin-top: calc((-1 * $padding) / 4);
                }
            }

            .name {
                font-size: 24px;
                color: $darkgrey2;
            }

            .address {
                font-size: 16px;
            }

            .email,
            .phone {
                color: $grey;

                a:hover {
                    color: $darkgrey2;
                }
            }
        }
    }

    .disponibilities {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: $padding;
        .disponibility {
            display: inline-flex;
            align-items: center;
            justify-content: space-between;
            background: $white;
            padding: calc($padding / 3);
            padding-left: 16px;
            margin-bottom: calc($padding / 4);
            border-radius: 30px;
            margin-right: 16px;
            &:last-child {
                margin-right: 0;
            }
            white-space: nowrap;
            border: 1px solid $lightgrey;

            .name {
                margin-right: 16px;
            }

            &.active {
                background: $purple;
                color: $white;
                font-weight: $medium;

                svg {
                    fill: $white;

                    .cls-2 {
                        fill: $purple;
                    }
                }
            }
        }
    }

    .availabilities {
        margin-bottom: $padding;
        .action {
            display: none;
        }
    }

    .jobs {
        .job {
            display: flex;
            justify-content: space-between;
            padding: 16px;
            margin-bottom: 16px;
            border-radius: 10px;
            border: 1px solid $grey2;

            .jobname {
                text-transform: lowercase;
                &:first-letter {
                    text-transform: uppercase;
                }
            }
        }
    }

    .job {
        flex-direction: column;
        .top-job {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
    }

    .jobs {
        margin-bottom: $padding;
    }

    .subtitle {
        margin-bottom: 16px;
        font-size: 18px;
        font-weight: 500;
    }
    .workerDocumentsGroup {
        margin-bottom: 2rem;
        .group-title {
            font-weight: $medium;
            margin-bottom: 1rem;
        }
        .workerDocuments {
            margin-bottom: 16px;

            .card-content {
                display: flex;
                justify-content: space-between;

                .habName {
                    font-weight: $medium;
                }

                .expired {
                    color: $error-600;
                }
            }
        }
    }
}
</style>
